<script setup lang="ts">
import type AntiRobot from '~/components/base/form/AntiRobot.vue';

interface Props {
  formPassword: string;
}

const props = defineProps<Props>();

const { $metrics } = useNuxtApp();
const { openLogin } = useAuth();
const { isAppSource } = useLayout();
const { login, resetRecoveryPasswordFlow, recoveryPasswordData } =
  useAuthStore();

const { isCaptchaLoginEnabled, isInvisibleCaptcha } = useCaptcha();

const isLoadingLogin = ref<boolean>(false);
const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed(() => {
  if (!isCaptchaLoginEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    return 'mt-0 relative';
  }

  return 'mt-2 -mb-4';
});

const canAutoLogin = computed(() => {
  return !isCaptchaLoginEnabled.value || !!antiRobotToken.value;
});

const redirectToHome = async () => {
  if (!canAutoLogin.value) {
    return;
  }

  if (recoveryPasswordData?.login && props.formPassword) {
    isLoadingLogin.value = true;

    const submitData: ILoginSubmitData = {
      login: recoveryPasswordData.login,
      email: recoveryPasswordData.login,
      password: props.formPassword,
      app_source: isAppSource.value ? 'app' : 'web',
      captcha_token: antiRobotToken.value,
    };

    await login(submitData)
      .then(async (response) => {
        $metrics.push('gtm')?.handle({
          event: 'login',
          ...gtmUserParams(response.data.user),
        });

        await navigateTo(localeLink('/'));
      })
      .catch(async () => {
        await navigateTo(localeLink('/'));
        openLogin();
      })
      .finally(() => {
        resetRecoveryPasswordFlow();
        antiRobotRef.value?.$resetAllCaptcha();
        isLoadingLogin.value = false;
      });
  }
};
</script>

<template>
  <section :class="$style.messageWrapper">
    <BaseLoading
      class="!bg-auth-bg-primary"
      :icon-loader="true"
      :show="isLoadingLogin"
    />

    <img
      alt="Email icon"
      aria-hidden="true"
      :class="$style.icon"
      :src="getAssetsUrlForCDN('/assets/images/svg/success_check.svg')"
    />

    <div :class="$style.textMessage">
      <p>{{ $t('auth.recover_password.success.title') }} </p>
      <span> {{ $t('auth.recover_password.success.subtitle') }} </span>
    </div>

    <div :class="captchaComponentClass">
      <BaseFormAntiRobot ref="antiRobotRef" />
    </div>

    <button
      :class="$style.btnSubmit"
      :disabled="!canAutoLogin || isLoadingLogin"
      type="button"
      @click="redirectToHome"
    >
      <NuxtIcon name="check" />
      {{ $t('auth.login.login') }}
    </button>
  </section>
</template>

<style module lang="scss">
.messageWrapper {
  @apply flex flex-col justify-center items-center space-y-6 my-4;

  .textMessage {
    @apply mt-8 text-center justify-center items-center;

    p {
      @apply text-texts font-semibold;
    }
    span {
      @apply text-texts text-xs;
    }
  }

  .icon {
    @apply w-28 h-28 mx-auto mt-5;
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed;
  }
}
</style>
